/**
 * Crop detail page — shared between v1 and v2.
 *
 * The /api/crops/:id response includes both the crop and its varieties.
 * System crops (ranch_id NULL, is_system=1) are read-only — the worker
 * rejects PATCH/DELETE for those rows. We surface that on the page by
 * hiding Edit/Archive when is_system === 1 and offering a Clone CTA
 * instead, matching the pattern Phase 1.5 templates use.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Copy, Edit2, Sparkles, Sprout, Trash2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/PageHeader";
import { EntityImageGallery } from "@/components/EntityImageGallery";
import { EditEntityDialog } from "@/components/v2/dialogs/EditEntityDialog";
import { DeleteEntityDialog } from "@/components/v2/dialogs/DeleteEntityDialog";
import type { FieldDef } from "@/components/v2/dialogs/AddEntityDialog";
import { authFetch } from "@/lib/auth";
import { toast } from "sonner";

type Category = "vegetable" | "fruit" | "grain" | "forage" | "herb" | "flower";
type Season = "cool" | "warm" | "year_round";

interface Crop {
  id: string;
  ranch_id: string | null;
  name: string;
  category: Category;
  growing_season: Season;
  is_system: number;
  description: string | null;
  image_key: string | null;
  created_at: string;
  updated_at: string;
}

interface Variety {
  id: string;
  crop_id: string;
  ranch_id: string | null;
  name: string;
  days_to_maturity: number | null;
  yield_per_unit: number | null;
  yield_unit: string | null;
  is_system: number;
}

const CATEGORY_LABEL: Record<Category, string> = {
  vegetable: "Vegetable",
  fruit: "Fruit",
  grain: "Grain",
  forage: "Forage",
  herb: "Herb",
  flower: "Flower",
};
const SEASON_LABEL: Record<Season, string> = {
  cool: "Cool season",
  warm: "Warm season",
  year_round: "Year-round",
};

const FIELDS: FieldDef[] = [
  { name: "name", label: "Name", required: true, placeholder: "Tomato" },
  {
    name: "category",
    label: "Category",
    type: "select",
    required: true,
    half: true,
    options: (Object.keys(CATEGORY_LABEL) as Category[]).map((v) => ({
      value: v,
      label: CATEGORY_LABEL[v],
    })),
  },
  {
    name: "growing_season",
    label: "Growing season",
    type: "select",
    required: true,
    half: true,
    options: (Object.keys(SEASON_LABEL) as Season[]).map((v) => ({
      value: v,
      label: SEASON_LABEL[v],
    })),
  },
  { name: "description", label: "Description", type: "textarea" },
];

function transformCrop(v: Record<string, string>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (v.name !== undefined) out.name = v.name;
  if (v.category !== undefined) out.category = v.category;
  if (v.growing_season !== undefined) out.growing_season = v.growing_season;
  if (v.description !== undefined) out.description = v.description || null;
  return out;
}

export function CropDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [crop, setCrop] = useState<Crop | null>(null);
  const [varieties, setVarieties] = useState<Variety[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [cloning, setCloning] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  const load = useCallback(async (): Promise<void> => {
    if (!id) return;
    try {
      const data = await authFetch<{ crop: Crop; varieties: Variety[] }>(`/api/crops/${id}`);
      setCrop(data.crop);
      setVarieties(data.varieties ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load crop");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load, refreshTick]);

  const handleClone = async (): Promise<void> => {
    if (!crop) return;
    setCloning(true);
    try {
      const cloned = await authFetch<Crop>(`/api/crops/${crop.id}/clone`, {
        method: "POST",
        body: JSON.stringify({}),
      });
      toast.success(`Cloned to ${cloned.name}`);
      navigate(`/crops/${cloned.id}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to clone crop");
    } finally {
      setCloning(false);
    }
  };

  if (loading) {
    return (
      <>
        <PageHeader
          title=""
          breadcrumbs={[
            { label: "Dashboard", href: "/" },
            { label: "Crops", href: "/crops" },
            { label: "Loading…" },
          ]}
        />
        <Skeleton className="h-48 w-full rounded-lg" />
      </>
    );
  }

  if (error || !crop) {
    return (
      <>
        <PageHeader
          title="Crop Not Found"
          breadcrumbs={[
            { label: "Dashboard", href: "/" },
            { label: "Crops", href: "/crops" },
            { label: "Error" },
          ]}
        />
        <div className="rounded-lg bg-destructive/10 p-4 text-sm text-destructive">
          {error || "Crop not found"}
        </div>
      </>
    );
  }

  const isSystem = crop.is_system === 1;

  return (
    <>
      <PageHeader
        title={crop.name}
        breadcrumbs={[
          { label: "Dashboard", href: "/" },
          { label: "Crops", href: "/crops" },
          { label: crop.name },
        ]}
      >
        {isSystem ? (
          <Button variant="outline" size="sm" onClick={handleClone} disabled={cloning}>
            <Copy className="mr-1 h-4 w-4" /> {cloning ? "Cloning…" : "Clone to ranch"}
          </Button>
        ) : (
          <>
            <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
              <Edit2 className="mr-1 h-4 w-4" /> Edit
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDeleteOpen(true)}
              className="text-destructive border-destructive/30"
            >
              <Trash2 className="mr-1 h-4 w-4" /> Archive
            </Button>
          </>
        )}
      </PageHeader>

      <div className="space-y-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2">
              <Sprout className="h-5 w-5 text-ranch-green" /> Crop Details
              {isSystem ? (
                <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-clay/10 px-2 py-0.5 font-mono text-2xs uppercase tracking-wider text-clay dark:bg-clay-dark/20 dark:text-clay-dark">
                  <Sparkles className="h-3 w-3" />
                  System
                </span>
              ) : null}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">Category</p>
                <p className="font-medium">{CATEGORY_LABEL[crop.category]}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Growing season</p>
                <p className="font-medium">{SEASON_LABEL[crop.growing_season]}</p>
              </div>
              {crop.description ? (
                <div className="col-span-2">
                  <p className="text-sm text-muted-foreground">Description</p>
                  <p className="text-sm whitespace-pre-wrap">{crop.description}</p>
                </div>
              ) : null}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Varieties ({varieties.length})</CardTitle>
          </CardHeader>
          <CardContent>
            {varieties.length === 0 ? (
              <p className="text-sm text-muted-foreground">No varieties yet.</p>
            ) : (
              <ul className="divide-y divide-border">
                {varieties.map((v) => (
                  <li key={v.id} className="flex items-center justify-between py-2 text-sm">
                    <span className="font-medium">{v.name}</span>
                    <span className="font-mono text-xs text-muted-foreground">
                      {v.days_to_maturity != null ? `${v.days_to_maturity}d to maturity` : "—"}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <EntityImageGallery entityType="crop" entityId={crop.id} />
          </CardContent>
        </Card>
      </div>

      {!isSystem ? (
        <>
          <EditEntityDialog
            open={editOpen}
            onOpenChange={setEditOpen}
            title={`Edit ${crop.name}`}
            endpoint="/api/crops"
            entityId={crop.id}
            fields={FIELDS}
            initialValues={{
              name: crop.name,
              category: crop.category,
              growing_season: crop.growing_season,
              description: crop.description ?? "",
            }}
            transform={transformCrop}
            onSaved={() => setRefreshTick((t) => t + 1)}
          />
          <DeleteEntityDialog
            open={deleteOpen}
            onOpenChange={setDeleteOpen}
            endpoint="/api/crops"
            entityId={crop.id}
            entityName={crop.name}
            onDeleted={() => navigate("/crops")}
          />
        </>
      ) : null}
    </>
  );
}
